import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { diffLines } from "diff";
import api, { DOC_META } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { GitCompare, Loader2, FileText, Plus, Minus } from "lucide-react";

const DOC_ORDER = ["hld", "lld", "asbuilt"];

export default function ProjectCompare() {
  const [params] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [psCode, setPsCode] = useState("");
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [docType, setDocType] = useState("");

  const [aText, setAText] = useState(null);
  const [bText, setBText] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [availableDocs, setAvailableDocs] = useState([]);

  useEffect(() => {
    api.get("/projects")
      .then(({ data }) => setProjects(data))
      .catch(() => toast.error("Failed to load projects"))
      .finally(() => setLoading(false));
  }, []);

  // groups with >= 2 versions
  const groups = useMemo(() => {
    const m = new Map();
    projects.forEach((p) => {
      const k = p.ps_code || "—";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    });
    return Array.from(m.entries())
      .filter(([, list]) => list.length >= 2)
      .map(([code, list]) => ({ code, list: [...list].sort((a, b) => (b.version || 0) - (a.version || 0)) }));
  }, [projects]);

  const currentGroup = useMemo(() => groups.find((g) => g.code === psCode), [groups, psCode]);

  // preselect from ?ps= or first group
  useEffect(() => {
    if (!groups.length) return;
    const q = params.get("ps");
    const initial = groups.find((g) => g.code === q) || groups[0];
    setPsCode((prev) => prev || initial.code);
  }, [groups, params]);

  // when group changes, default to two latest versions
  useEffect(() => {
    if (!currentGroup) return;
    setAId(currentGroup.list[0]?.id || "");
    setBId(currentGroup.list[1]?.id || "");
  }, [currentGroup]);

  // resolve available doc types shared by both selected versions
  useEffect(() => {
    if (!aId || !bId) { setAvailableDocs([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const [a, b] = await Promise.all([api.get(`/projects/${aId}`), api.get(`/projects/${bId}`)]);
        const aTypes = new Set((a.data.documents || []).map((d) => d.doc_type));
        const bTypes = new Set((b.data.documents || []).map((d) => d.doc_type));
        const shared = DOC_ORDER.filter((t) => aTypes.has(t) && bTypes.has(t));
        if (!cancelled) {
          setAvailableDocs(shared);
          setDocType((prev) => (shared.includes(prev) ? prev : shared[0] || ""));
        }
      } catch { if (!cancelled) setAvailableDocs([]); }
    })();
    return () => { cancelled = true; };
  }, [aId, bId]);

  // load both document contents for the chosen doc type
  useEffect(() => {
    if (!aId || !bId || !docType) { setAText(null); setBText(null); return; }
    let cancelled = false;
    setDocLoading(true);
    const fetchContent = async (pid) => {
      const { data } = await api.get(`/projects/${pid}`);
      const meta = (data.documents || []).find((d) => d.doc_type === docType);
      if (!meta) return "";
      const doc = await api.get(`/projects/${pid}/documents/${meta.id}`);
      return doc.data.content || "";
    };
    Promise.all([fetchContent(aId), fetchContent(bId)])
      .then(([a, b]) => { if (!cancelled) { setAText(a); setBText(b); } })
      .catch(() => { if (!cancelled) toast.error("Failed to load documents"); })
      .finally(() => { if (!cancelled) setDocLoading(false); });
    return () => { cancelled = true; };
  }, [aId, bId, docType]);

  const diff = useMemo(() => {
    if (aText == null || bText == null) return null;
    return diffLines(aText, bText);
  }, [aText, bText]);

  const { leftLines, rightLines, addCount, delCount } = useMemo(() => {
    if (!diff) return { leftLines: [], rightLines: [], addCount: 0, delCount: 0 };
    const left = [], right = [];
    let adds = 0, dels = 0;
    diff.forEach((part) => {
      const lines = part.value.replace(/\n$/, "").split("\n");
      if (part.added) {
        lines.forEach((l) => { right.push({ t: "add", l }); adds++; });
      } else if (part.removed) {
        lines.forEach((l) => { left.push({ t: "del", l }); dels++; });
      } else {
        lines.forEach((l) => { left.push({ t: "ctx", l }); right.push({ t: "ctx", l }); });
      }
    });
    return { leftLines: left, rightLines: right, addCount: adds, delCount: dels };
  }, [diff]);

  const versionLabel = (p) => `v${p.version} — ${p.name}${p.owner_initials ? ` (${p.owner_initials})` : ""}`;

  const Pane = ({ title, lines, side }) => (
    <div className="flex-1 min-w-0 border border-white/10 bg-[#0B0C10]">
      <div className="px-4 py-2.5 border-b border-white/10 font-mono-jb text-xs text-white truncate flex items-center gap-2">
        <FileText size={13} className={side === "a" ? "text-[#FF3B30]" : "text-[#00FF41]"} /> {title}
      </div>
      <pre className="overflow-auto max-h-[62vh] text-[11px] leading-[1.6] font-mono-jb m-0" data-testid={`compare-pane-${side}`}>
        {lines.map((ln, i) => (
          <div key={i}
            className={
              ln.t === "del" ? "bg-[#FF3B30]/12 text-[#FF9A93] px-3" :
              ln.t === "add" ? "bg-[#00FF41]/12 text-[#7CFFA0] px-3" :
              "text-[#8B949E] px-3"
            }>
            <span className="select-none mr-2 opacity-50">{ln.t === "del" ? "-" : ln.t === "add" ? "+" : " "}</span>
            {ln.l || "\u00A0"}
          </div>
        ))}
      </pre>
    </div>
  );

  return (
    <div className="p-8 lg:p-10">
      <div className="border-b border-white/10 pb-6 mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">Admin</div>
        <h1 className="font-mono-jb text-3xl sm:text-4xl font-extrabold tracking-tighter text-white flex items-center gap-3">
          <GitCompare className="text-[#00F0FF]" size={30} /> Version Compare
        </h1>
        <p className="text-[#8B949E] mt-3 leading-relaxed">Compare two versions that share the same PS Code, side by side, to spot exactly what changed.</p>
      </div>

      {loading ? (
        <div className="font-mono-jb text-[#8B949E] text-sm"><Loader2 className="animate-spin inline mr-2" size={15} /> Loading...</div>
      ) : groups.length === 0 ? (
        <div className="border border-dashed border-white/10 bg-[#0B0C10] p-12 text-center" data-testid="compare-empty">
          <GitCompare className="mx-auto text-[#484F58] mb-4" size={44} />
          <div className="font-mono-jb text-lg text-white mb-1">Nothing to compare yet</div>
          <p className="text-[#8B949E] text-sm max-w-md mx-auto">Version comparison appears once two or more projects share the same PS Code.</p>
        </div>
      ) : (
        <>
          {/* Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">PS Code</label>
              <Select value={psCode} onValueChange={setPsCode}>
                <SelectTrigger data-testid="compare-pscode-select" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus:ring-[#00F0FF]">
                  <SelectValue placeholder="Select PS Code" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                  {groups.map((g) => (
                    <SelectItem key={g.code} value={g.code}>{g.code} ({g.list.length})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#FF3B30] font-mono-jb">Version A</label>
              <Select value={aId} onValueChange={setAId}>
                <SelectTrigger data-testid="compare-version-a" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus:ring-[#FF3B30]">
                  <SelectValue placeholder="Version A" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                  {currentGroup?.list.map((p) => <SelectItem key={p.id} value={p.id}>{versionLabel(p)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#00FF41] font-mono-jb">Version B</label>
              <Select value={bId} onValueChange={setBId}>
                <SelectTrigger data-testid="compare-version-b" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus:ring-[#00FF41]">
                  <SelectValue placeholder="Version B" />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                  {currentGroup?.list.map((p) => <SelectItem key={p.id} value={p.id}>{versionLabel(p)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Document</label>
              <Select value={docType} onValueChange={setDocType} disabled={!availableDocs.length}>
                <SelectTrigger data-testid="compare-doc-select" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus:ring-[#00F0FF]">
                  <SelectValue placeholder={availableDocs.length ? "Select document" : "No shared documents"} />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0C10] border-white/15 text-white font-mono-jb">
                  {availableDocs.map((t) => <SelectItem key={t} value={t}>{DOC_META[t]?.full || t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {aId && bId && aId === bId && (
            <div className="text-[#EAE04A] font-mono-jb text-xs mb-4" data-testid="compare-same-warning">Pick two different versions to compare.</div>
          )}

          {docLoading ? (
            <div className="font-mono-jb text-[#8B949E] text-sm"><Loader2 className="animate-spin inline mr-2" size={15} /> Loading documents...</div>
          ) : !docType ? (
            <div className="text-[#484F58] font-mono-jb text-sm border border-dashed border-white/10 p-8 text-center">
              These two versions have no generated document type in common. Generate the same document type in both to compare.
            </div>
          ) : diff ? (
            <>
              <div className="flex items-center gap-5 mb-4 font-mono-jb text-xs" data-testid="compare-summary">
                <span className="flex items-center gap-1.5 text-[#7CFFA0]"><Plus size={13} /> {addCount} added</span>
                <span className="flex items-center gap-1.5 text-[#FF9A93]"><Minus size={13} /> {delCount} removed</span>
                {addCount === 0 && delCount === 0 && <span className="text-[#8B949E]">No differences in this document.</span>}
              </div>
              <div className="flex flex-col lg:flex-row gap-5">
                <Pane title={`Version A · ${currentGroup?.list.find((p) => p.id === aId)?.name || ""}`} lines={leftLines} side="a" />
                <Pane title={`Version B · ${currentGroup?.list.find((p) => p.id === bId)?.name || ""}`} lines={rightLines} side="b" />
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
